/**
 * 生徒API
 */
import { Component, Vue } from 'vue-property-decorator'
import { ApiResponseStudentList, ApiResponseStudentEditData } from '@/models/apiResponses/students'
import { StudentSearchParamsType, StudentEditType } from '@/models/studentListModel'
import { UserRoles } from '@/const/UserRoles'

@Component
export default class StudentsApi extends Vue {
  /**
   * 生徒一覧取得
   * @param params 検索パラメーター群
   * @param role ユーザー権限
   * @returns ページネーション情報付き生徒一覧情報
   */
  protected async searchStudents(params: StudentSearchParamsType, role: UserRoles): Promise<ApiResponseStudentList> {
    const { data }: { data: ApiResponseStudentList } = await Vue.prototype.$http.httpWithToken
      .get(`/v2/${role}/students/search`, { params: params })
      .catch(() => ({ data: { responses: [], pagination: null } }))

    return data
  }

  /**
   * 生徒情報取得
   * @param id 生徒ID
   * @param role ユーザー権限
   * @returns 生徒情報
   */
  protected async getStudentData(id: number, role: UserRoles): Promise<ApiResponseStudentEditData> {
    const { data }: { data: ApiResponseStudentEditData } = await Vue.prototype.$http.httpWithToken.get(
      `/v2/${role}/students/${id}`
    )

    return data
  }

  /**
   * 生徒情報登録
   * @param editData 生徒編集情報
   * @param role ユーザー権限
   */
  protected async createStudentData(editData: StudentEditType, role: UserRoles): Promise<void> {
    await Vue.prototype.$http.httpWithToken.post(`/v2/${role}/students`, {
      academyId: Number(editData.academyId),
      branchId: Number(editData.branchId),
      gradeId: Number(editData.gradeId),
      userName: editData.userName,
      bcbtCode: editData.bcbtCode,
      password: editData.password,
      passwordConfirm: editData.passwordConfirm,
      statusName: editData.status,
    })
  }

  /**
   * 生徒情報更新
   * @param editData 生徒更新情報
   * @param role ユーザー権限
   */
  protected async updateStudentData(editData: StudentEditType, role: UserRoles): Promise<void> {
    await Vue.prototype.$http.httpWithToken.put(`/v2/${role}/students/${editData.id}`, {
      gradeId: Number(editData.gradeId),
      userName: editData.userName,
      password: editData.password,
      passwordConfirm: editData.passwordConfirm,
      statusName: editData.status,
    })
  }

  /**
   * 生徒情報削除
   * @param id 生徒ID
   * @param role ユーザー権限
   */
  protected async deleteStudentData(id: number, role: UserRoles): Promise<void> {
    await Vue.prototype.$http.httpWithToken.delete(`/v2/${role}/students/${id}`)
  }

  /**
   * 生徒一覧のエクセルダウンロード
   * @param params 検索パラメーター群
   * @param studentIds ダウンロード対象の生徒ID
   * @param role ユーザー権限
   * @returns エクセルデータ
   */
  protected async exportStudentExcel(
    params: StudentSearchParamsType,
    studentIds: number[],
    role: UserRoles
  ): Promise<Blob> {
    const { data }: { data: Blob } = await Vue.prototype.$http.httpWithToken.get(`/v2/${role}/students/export`, {
      params: Object.assign(params, { studentIds: studentIds }),
      responseType: 'blob',
      dataType: 'binary',
    })

    return data
  }

  /**
   * 生徒一覧エクセルアップロード
   * @param file エクセルファイル
   * @param role ユーザー権限
   */
  protected async importStudentExcel(file: File, role: UserRoles): Promise<void> {
    const headers = { 'Content-Type': 'multipart/form-data' }
    const params = new FormData()
    params.append('excel', file)
    await Vue.prototype.$http.httpWithToken.post(`/v2/${role}/students/import`, params, {
      headers: headers,
    })
  }

  /**
   * 生徒QRログイン用PDFダウンロード
   * @param params 検索パラメーター群
   * @param studentIds ダウンロード対象の生徒ID
   * @param role ユーザー権限
   * @returns pdfデータ
   */
  protected async downloadStudentQrLoginPdf(
    params: StudentSearchParamsType,
    studentIds: number[],
    role: UserRoles
  ): Promise<Blob> {
    const { data }: { data: Blob } = await Vue.prototype.$http.httpWithToken.get(`/v2/${role}/students/download_pdf`, {
      params: Object.assign(params, { studentIds: studentIds }),
      responseType: 'blob',
      dataType: 'binary',
    })

    return data
  }
}
